import { AuthContext } from '@/context/auth-context'
import DashboardAuth from '@/routes/dashboard/auth/DashboardAuth'
import { ReactNode, useContext } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { signInPathFor } from '@/auth/utils/auth-redirect'

type AnonFeatureGuardProps = {
  children: ReactNode
  showAuthOnGuard: boolean
}

/**
 * Anonymous Feature Guard
 * With `showAuthOnGuard`, anonymous users get the in-page sign in / sign up screen, or are sent to
 * `/sign-in` with the requested page when `ONBOARDING_REFRESH_AUTH_PAGES` is on. Without it, they
 * see nothing.
 */
export default function AnonFeatureGuard(props: AnonFeatureGuardProps) {
  const { children, showAuthOnGuard } = props
  const location = useLocation()

  // Auth context
  const authContext = useContext(AuthContext)
  const user = authContext?.user
  const userIsAnon = user?.isAnonymous

  // Allow non-anonymous users through
  if (userIsAnon == false) {
    return children
  }

  if (showAuthOnGuard && kGetFeatureFlagStatus(kFeatureFlags.ONBOARDING_REFRESH_AUTH_PAGES)) {
    // Before the anonymous session exists, a redirect would also bounce users who are signed in.
    if (user == null) return <FullPageLoader />
    return <Navigate to={signInPathFor(location)} replace />
  }

  // Return the anonymous guard UX for all features except those allowed
  return showAuthOnGuard ? <DashboardAuth /> : null
}
